import React, { useEffect, useState } from "react";
import { ethers } from "ethers";
import abi from "../../abi/airdrop.json";
import "../../gamble.css";

const CONTRACT = "0xee4155CD0FC2a67aFF0beb0BBBE3A4EBeE26416E";

const RPC = "https://api.avax.network/ext/bc/C/rpc";

const addressLink = (addr) => `https://snowtrace.io/address/${addr}`;

export default function Airdrop() {
  const [address, setAddress] = useState("");
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState("");
  const [balance, setBalance] = useState("0");

  // ------------------------------------------------
  // HELPERS
  // ------------------------------------------------

  const isMobile = () => {
    return /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
  };

  const isMetaMaskAvailable = () => {
    return (
      typeof window !== "undefined" && typeof window.ethereum !== "undefined"
    );
  };

  const openMetaMaskMobile = () => {
    const currentUrl = window.location.href;

    const deepLink =
      `https://metamask.app.link/dapp/` +
      currentUrl.replace(/^https?:\/\//, "");

    window.location.href = deepLink;
  };

  // ------------------------------------------------
  // LOAD BALANCE
  // ------------------------------------------------

  const loadBalance = async () => {
    try {
      const provider = new ethers.JsonRpcProvider(RPC);
      const contract = new ethers.Contract(CONTRACT, abi, provider);

      const bal = await contract.contractBalance();
      setBalance(Number(ethers.formatEther(bal)).toFixed(2));
    } catch (e) {
      console.log(e);
    }
  };

  useEffect(() => {
    loadBalance();
  }, []);

  // ------------------------------------------------
  // CLAIM
  // ------------------------------------------------

  const claim = async () => {
    try {
      setStatus("");

      // --------------------------------------------
      // ADDRESS CHECK
      // --------------------------------------------

      if (!ethers.isAddress(address)) {
        setStatus("Invalid address");
        return;
      }

      // --------------------------------------------
      // WALLET CHECK
      // --------------------------------------------

      if (!isMetaMaskAvailable()) {
        if (isMobile()) {
          setStatus("Opening MetaMask app...");
          openMetaMaskMobile();
          return;
        }

        setStatus("MetaMask not installed");
        return;
      }

      setLoading(true);

      // --------------------------------------------
      // NETWORK SWITCH
      // --------------------------------------------

      try {
        await window.ethereum.request({
          method: "wallet_switchEthereumChain",
          params: [{ chainId: "0xa86a" }],
        });
      } catch (switchError) {
        setStatus("Wrong network (switch to Avalanche)");
        setLoading(false);
        return;
      }

      // --------------------------------------------
      // PROVIDER
      // --------------------------------------------

      const provider = new ethers.BrowserProvider(window.ethereum);
      await provider.send("eth_requestAccounts", []);
      const signer = await provider.getSigner();

      const contract = new ethers.Contract(CONTRACT, abi, signer);

      // --------------------------------------------
      // AIRDROP BALANCE CHECK
      // --------------------------------------------

      const bal = await contract.contractBalance();

      if (Number(ethers.formatEther(bal)) < 50000) {
        setStatus("Airdrop empty");
        setLoading(false);
        return;
      }

      // --------------------------------------------
      // TX
      // --------------------------------------------

      setStatus("Confirm in MetaMask...");

      // OPTIONAL gas estimation (safe version, no args!)
      let gasLimit;
      try {
        const gas = await contract.claim.estimateGas();
        gasLimit = (gas * 120n) / 100n;
      } catch (e) {
        console.log("Gas estimation failed, using default");
      }

      const tx = await contract.claim({
        ...(gasLimit ? { gasLimit } : {}),
      });

      setStatus("Waiting confirmation...");

      await tx.wait();

      setStatus("50000 G-ETTO sent");

      setAddress("");

      loadBalance();
    } catch (e) {
      console.log(e);

      if (e.code === 4001) {
        setStatus("User rejected");
      } else if (e.reason) {
        setStatus(e.reason);
      } else if (e.shortMessage) {
        setStatus(e.shortMessage);
      } else if (e.message?.includes("insufficient funds")) {
        setStatus("Not enough AVAX for gas");
      } else {
        setStatus("Transaction failed");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="airdropBox">
      <h2>AIRDROP</h2>

      <p>Contract Balance: {balance} G-ETTO</p>

      <p>
        <a
          href={addressLink(CONTRACT)}
          target="_blank"
          rel="noopener noreferrer"
          className="address-link"
          style={{
            wordBreak: "break-all",
            overflowWrap: "break-word",
          }}
        >
          {CONTRACT}
        </a>
      </p>

      <p>The contract distributes 50000 G-ETTO per wallet.</p>
      <p>Each address can claim the airdrop only once.</p>

      {/* MOBILE BUTTON */}
      {!isMetaMaskAvailable() && isMobile() && (
        <button
          onClick={openMetaMaskMobile}
          className="airdropButton"
          style={{ marginBottom: "12px" }}
        >
          OPEN IN METAMASK
        </button>
      )}

      <input
        type="text"
        value={address}
        onChange={(e) => setAddress(e.target.value)}
        placeholder="0x..."
        className="nameInput"
      />

      <p>{status}</p>

      <button onClick={claim} disabled={loading} className="airdropButton">
        {loading ? "PROCESSING..." : "TRANSACT"}
      </button>
    </div>
  );
}
